import { Box, Grid, styled, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { useContext, useEffect } from "react";
import { useCookies } from "react-cookie";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import WarningIcon from "../../assets/images/warning_icon.png";
import CustomButton from "../../components/button/custom-button.component";
import routes from "../../components/navigation/routes";
import { SnackbarContext } from "../../components/notification/snackbar.context";
import Spacer from "../../components/utils/spacer.component";
import { isLogin } from "../../infrastructure/utils";
import {
  cancelTopUpBill,
  checkTopUpBill,
  creditSelector,
} from "../../services/credit/credit-slice.service";
import {
  cancelEventBookingBill,
  eventBookingBillSelector,
  getEventBillViaRemoteBillId,
} from "../../services/event/booking/bill/event-booking-bill-slice.service";
import {
  cancelFitnessClassSessionBookingBill,
  fitnessClassSessionBookingBillSelector,
  getFitnessClassSessionBookingViaRemoteBillId,
} from "../../services/fitness_class/session/booking/bill/fitness-class-session-booking-bill-slice.service";
import {
  cancelHourlyBookingBill,
  getHourlyBookingViaRemoteBillId,
  hourlyBookingBillSelector,
} from "../../services/hourly-booking/bill/hourly-booking-bill-slice.service";
import {
  cancelPackageBill,
  getPackageBillViaRemoteBillId,
  packageBillSelector,
} from "../../services/package/bill/package-bill-slice.service";
import {
  cancelSubscriptionBill,
  getSubscriptionBillViaRemoteBillId,
  subscriptionBillSelector,
} from "../../services/subscription/bill/subscription-bill-slice.service";
import {
  cancelMembershipBill,
  getMembershipBillViaRemoteBillId,
  membershipBillSelector,
} from "../../services/membership/bill/membership-bill-slice.service";
import {
  cancelSportBookingBill,
  getSportBillViaRemoteBillId,
  sportBookingBillSelector,
} from "../../services/sport/booking/bill/sport-booking-bill-slice.service";
import {
  checkPaymentRequestBill,
  cancelPaymentRequestBill,
  paymentRequestBillSelector,
} from "../../services/payment-request/bill/payment-request-bill-slice.service";

const StyledContainer = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexDirection: "column",
  paddingBottom: "50px",
});

const StyledTitle = styled(Typography)(({ theme }) => ({
  fontWeight: theme.fonts.fontWeights.bold,
  color: theme.palette.colors.text.primary,
  textAlign: "center",
}));

const GreyText = styled(Typography)(({ theme }) => ({
  color: theme.palette.colors.text.secondary,
  textAlign: "center",
}));

export default function BillPaymentFailed() {
  const theme = useTheme();
  const createSnackBar = useContext(SnackbarContext);
  const [cookies, setCookie, removeCookie] = useCookies(["guest-payment-token"]); // eslint-disable-line no-unused-vars
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { checkTopUpBillObj, cancelTopUpBillObj } = useSelector(creditSelector);
  const { getHourlyBookingViaRemoteBillIdObj, cancelHourlyBookingBillObj } =
    useSelector(hourlyBookingBillSelector);
  const { getSportBillViaRemoteBillIdObj, cancelSportBookingBillObj } =
    useSelector(sportBookingBillSelector);
  const { getSubscriptionBillViaRemoteBillIdObj, cancelSubscriptionBillObj } =
    useSelector(subscriptionBillSelector);
  const {
    cancelFitnessClassSessionBookingBillObj,
    getFitnessClassSessionBookingViaRemoteBillIdObj,
  } = useSelector(fitnessClassSessionBookingBillSelector);

  const { cancelPackageBillObj, getPackageBillViaRemoteBillIdObj } =
    useSelector(packageBillSelector);

  const { cancelEventBookingBillObj, getEventBillViaRemoteBillIdObj } =
    useSelector(eventBookingBillSelector);

  const { cancelMembershipBillObj, getMembershipBillViaRemoteBillIdObj } =
    useSelector(membershipBillSelector);
  const { checkPaymentRequestBillObj, cancelPaymentRequestBillObj } = useSelector(
    paymentRequestBillSelector,
  );

  const getCheckBillFunction = () => {
    switch (location.pathname) {
      case routes.TOPUPBILLPAYMENTFAILED:
        return checkTopUpBill;
      case routes.HOURLYBOOKINGPAYMENTFAILED:
        return getHourlyBookingViaRemoteBillId;
      case routes.SPORT_BOOKING_PAYMENT_FAILED:
        return getSportBillViaRemoteBillId;
      case routes.SUBSCRIPTIONPLANPAYMENTFAILED:
        return getSubscriptionBillViaRemoteBillId;
      case routes.MEMBERSHIP_PLAN_PAYMENT_FAILED:
        return getMembershipBillViaRemoteBillId;
      case routes.FITNESSCLASSSESSIONBOOKINGPAYMENTFAILED:
        return getFitnessClassSessionBookingViaRemoteBillId;
      case routes.PACKAGEPAYMENTFAILED:
        return getPackageBillViaRemoteBillId;
      case routes.EVENTBOOKINGPAYMENTFAILED:
        return getEventBillViaRemoteBillId;
      case routes.PAYMENT_REQUEST_BILL_FAILED:
        return checkPaymentRequestBill;
      default:
        return false;
    }
  };

  const getCancelBillFunction = () => {
    switch (location.pathname) {
      case routes.TOPUPBILLPAYMENTFAILED:
        return cancelTopUpBill;
      case routes.HOURLYBOOKINGPAYMENTFAILED:
        return cancelHourlyBookingBill;
      case routes.SPORT_BOOKING_PAYMENT_FAILED:
        return cancelSportBookingBill;
      case routes.SUBSCRIPTIONPLANPAYMENTFAILED:
        return cancelSubscriptionBill;
      case routes.MEMBERSHIP_PLAN_PAYMENT_FAILED:
        return cancelMembershipBill;
      case routes.FITNESSCLASSSESSIONBOOKINGPAYMENTFAILED:
        return cancelFitnessClassSessionBookingBill;
      case routes.PACKAGEPAYMENTFAILED:
        return cancelPackageBill;
      case routes.EVENTBOOKINGPAYMENTFAILED:
        return cancelEventBookingBill;
      case routes.PAYMENT_REQUEST_BILL_FAILED:
        return cancelPaymentRequestBill;
      default:
        return false;
    }
  };

  const getBillObj = () => {
    switch (location.pathname) {
      case routes.TOPUPBILLPAYMENTFAILED:
        return checkTopUpBillObj;
      case routes.HOURLYBOOKINGPAYMENTFAILED:
        return getHourlyBookingViaRemoteBillIdObj;
      case routes.SPORT_BOOKING_PAYMENT_FAILED:
        return getSportBillViaRemoteBillIdObj;
      case routes.SUBSCRIPTIONPLANPAYMENTFAILED:
        return getSubscriptionBillViaRemoteBillIdObj;
      case routes.MEMBERSHIP_PLAN_PAYMENT_FAILED:
        return getMembershipBillViaRemoteBillIdObj;
      case routes.FITNESSCLASSSESSIONBOOKINGPAYMENTFAILED:
        return getFitnessClassSessionBookingViaRemoteBillIdObj;
      case routes.PACKAGEPAYMENTFAILED:
        return getPackageBillViaRemoteBillIdObj;
      case routes.EVENTBOOKINGPAYMENTFAILED:
        return getEventBillViaRemoteBillIdObj;
      case routes.PAYMENT_REQUEST_BILL_FAILED:
        return checkPaymentRequestBillObj;
      default:
        return false;
    }
  };

  const getCancelBillObj = () => {
    switch (location.pathname) {
      case routes.TOPUPBILLPAYMENTFAILED:
        return cancelTopUpBillObj;
      case routes.HOURLYBOOKINGPAYMENTFAILED:
        return cancelHourlyBookingBillObj;
      case routes.SPORT_BOOKING_PAYMENT_FAILED:
        return cancelSportBookingBillObj;
      case routes.SUBSCRIPTIONPLANPAYMENTFAILED:
        return cancelSubscriptionBillObj;
      case routes.MEMBERSHIP_PLAN_PAYMENT_FAILED:
        return cancelMembershipBillObj;
      case routes.FITNESSCLASSSESSIONBOOKINGPAYMENTFAILED:
        return cancelFitnessClassSessionBookingBillObj;
      case routes.PACKAGEPAYMENTFAILED:
        return cancelPackageBillObj;
      case routes.EVENTBOOKINGPAYMENTFAILED:
        return cancelEventBookingBillObj;
      case routes.PAYMENT_REQUEST_BILL_FAILED:
        return cancelPaymentRequestBillObj;
      default:
        return false;
    }
  };

  useEffect(() => {
    const checkBillFunction = getCheckBillFunction();
    dispatch(
      checkBillFunction({
        ...location.state,
        token: isLogin() ? null : cookies["guest-payment-token"],
      }),
    );
  }, []);

  const handleCancelBill = () => {
    const cancelBillFunction = getCancelBillFunction();
    const billObj = getBillObj();

    dispatch(
      cancelBillFunction({
        billId: billObj.data.bill.id,
        token: isLogin() ? null : cookies["guest-payment-token"],
      }),
    ).then(({ meta, error, payload }) => {
      if (meta.requestStatus === "fulfilled") {
        createSnackBar({
          message: payload.message,
          type: "success",
          open: true,
        });
        navigate(routes.HOME);
      } else if (meta.requestStatus === "rejected") {
        createSnackBar({
          message: error.message,
          type: "error",
          open: true,
        });
      }
    });
  };

  return (
    <StyledContainer>
      <Spacer size="xxl" />
      <img src={WarningIcon} alt="warningIcon" width={170} />
      <Spacer size="xl" />
      <StyledTitle variant={isMobile ? "h4" : "h3"}>Payment Failed!</StyledTitle>
      <Spacer size="m" />
      <GreyText>
        Opps…Seems like something is wrong while the payment is processing. Please try again later.
      </GreyText>
      <Spacer size="xl" />
      <Grid container justifyContent="center">
        <Grid item>
          <CustomButton
            isLoading={getBillObj().status === "pending"}
            onClick={() => window.open(getBillObj().data.bill.remoteUrl, "_self")}
            width="220px"
            fontSize={theme.fonts.fontSizes.size18}
          >
            Retry
          </CustomButton>
        </Grid>
      </Grid>
      <Spacer size="m" />
      <Grid container justifyContent="center">
        <Grid item>
          <CustomButton
            isLoading={getBillObj().status === "pending" || getCancelBillObj().status === "pending"}
            onClick={handleCancelBill}
            width="220px"
            fontSize={theme.fonts.fontSizes.size18}
            buttonColor="white"
            textColor="primary"
          >
            Cancel
          </CustomButton>
        </Grid>
      </Grid>
    </StyledContainer>
  );
}
